/**
 * Mock data of the web chat (mock mode): the visitor card of the sample web chats and the team settings.
 * Kept apart from mockData.ts so the web chat stays one small, removable piece.
 */
import type { WebchatQuestion, WebchatSettings, WebchatSettingsResponse, WebContext, WebOrders, WebVisitorInfo } from './types'

const MIN = 60_000
const DAY_MS = 24 * 60 * MIN

/** YYYY-MM-DD in Bangkok, `days` from now (POS dates are Bangkok calendar days). */
function bkkDay(now: number, days: number): string {
  return new Date(now + days * DAY_MS + 7 * 3600_000).toISOString().slice(0, 10)
}

/** A visitor at the address/payment step with a full cart, a paused delivery area and a coupon that failed. */
function richContext(now: number): WebContext {
  const iso = (msAgo: number) => new Date(now - msAgo).toISOString()
  const page = (path: string) => `https://cleanfitdelivery.com/order/beta/${path}`
  return {
    updated_at: iso(40_000),
    step: 'checkout',
    step_since: iso(6 * MIN),
    pack: { name: 'Pro 7 day 21 มื้อ ลด 5%', meals: 21 },
    cart: {
      chosen: 21,
      total: 21,
      items: [
        { name: 'P1.กะเพราไก่สับ', qty: 4 },
        { name: 'P2.ไก่ย่างโคชูจัง', qty: 4 },
        { name: 'แซลมอนย่างซอสเทริยากิ', qty: 3 },
        { name: 'ข้าวผัดต้มยำกุ้ง', qty: 3 },
        { name: 'สเต็กอกไก่พริกไทยดำ', qty: 3 },
        { name: 'ผัดไทยกุ้งสด', qty: 2 },
        { name: 'ไก่คั่วพริกเกลือ', qty: 2 },
      ],
    },
    mode: 'new',
    member: { logged_in: true, name: 'ฝน ใจดี', phone: '081-234-5678' },
    ad: { utm: { source: 'facebook', medium: 'cpc', campaign: 'sep-7day' }, click: 'facebook', referrer: 'https://m.facebook.com/' },
    trail: [
      { url: page('home?utm_source=facebook&utm_medium=cpc&utm_campaign=sep-7day'), title: 'เลือกแพ็กเกจ', at: iso(26 * MIN) },
      { url: page('menu'), title: 'เลือกเมนู', at: iso(21 * MIN) },
      { url: page('cart'), title: 'ตะกร้าสินค้า', at: iso(8 * MIN) },
      { url: page('checkout'), title: 'ที่อยู่และชำระเงิน', at: iso(6 * MIN) },
    ],
    events: [
      { kind: 'coupon', level: 'warn', text: 'โค้ด SEP10: โค้ดส่วนลดนี้หมดอายุแล้ว', at: iso(70_000) },
      { kind: 'ship_quote', level: 'info', text: 'ค่าจัดส่ง ฿150 · จัดส่ง อ. 29 ก.ย. · DHL · ได้รับภายในวันพุธที่ 30 กันยายน 2569', at: iso(3 * MIN) },
      { kind: 'area', level: 'error', text: 'งดส่ง Lalamove ชั่วคราว (น้ำท่วม) ตั้งแต่ 26 ก.ย. · บางนา · ยังไม่มีกำหนดวันเปิดส่ง — ติดต่อแอดมินทางแชทเพื่อจองคิวส่งเมื่อพื้นที่เปิด', at: iso(4 * MIN) },
      { kind: 'cutoff', level: 'warn', text: 'เลยเวลา 21:30 น. แล้ว จัดส่งพรุ่งนี้ไม่ทัน กรุณาเลือกวันส่งใหม่', at: iso(5 * MIN) },
    ],
  }
}

/** "คำสั่งซื้อของลูกค้านี้" for the mock web chats (c107: logged in on the site — an unpaid order + history · others:
 *  no customer the site vouched for). */
export function mockWebOrders(id: string, v: WebVisitorInfo | null, now: number): WebOrders {
  if (id !== 'c107' || !v?.identified) return { state: 'no_customer', who: [] }
  return {
    state: 'ok',
    who: ['login'],
    customers: [{ id: '7001', name: 'ฝน ใจดี', since: '2025-02-01 10:00:00' }],
    unpaid: [{
      ref: 'BBIRRO', total: 1305, delivery_date: bkkDay(now, 3), delivery_time: '11:00', status_text: null,
      cancelled: false, pack: 'Pro 7 day 21 มื้อ ลด 5%', meals: 21, created_at: new Date(now - 7 * MIN).toISOString().slice(0, 19).replace('T', ' '),
      method: 'promptpay', expires_at: `${bkkDay(now, 2)} 22:00:00`, expired: false,
    }],
    latest: {
      ref: '20260920-05', total: 1265, delivery_date: '2026-09-20', delivery_time: '11:00', status_text: 'ส่งสำเร็จ',
      cancelled: false, pack: 'Pro 7 day 21 มื้อ ลด 5%', meals: 21, created_at: '2026-09-19 12:00:00',
    },
    stats: { purchases: 7, deliveries: 12, total: 8430, first_at: '2025-02-01 10:30:00', last_at: '2026-09-19 12:00:00', partial: false },
    partial: false,
    fetched_at: new Date(now).toISOString(),
  }
}

/** Visitor cards by mock conversation id (see mockData.ts: c107 needs reply, c407 closed). */
export function mockWebVisitors(now: number): Map<string, WebVisitorInfo> {
  const iso = (msAgo: number) => new Date(now - msAgo).toISOString()
  return new Map<string, WebVisitorInfo>([
    [
      'c107',
      {
        site: 'cleanfitdelivery.com',
        name: null,
        phone: '081-234-5678',
        current_page: { url: 'https://cleanfitdelivery.com/order/beta/checkout', title: 'ที่อยู่และชำระเงิน' },
        first_page: 'https://cleanfitdelivery.com/order/beta/home?utm_source=facebook&utm_medium=cpc&utm_campaign=sep-7day',
        referrer: 'https://m.facebook.com/',
        utm: { source: 'facebook', medium: 'cpc', campaign: 'sep-7day' },
        last_utm: null,
        device: 'mobile',
        identified: true,
        email: 'fon.jaidee@example.com',
        email_opt_out: false,
        email_notice: { state: 'queued', at: iso(-2 * MIN), reason: 'online', reason_text: 'ลูกค้ายังเปิดหน้าเว็บอยู่' },
        first_seen_at: iso(26 * MIN),
        last_seen_at: iso(1 * MIN),
        context: richContext(now),
      },
    ],
    [
      'c407',
      {
        site: 'cleanfitdelivery.com',
        name: null,
        phone: null,
        current_page: { url: 'https://cleanfitdelivery.com/order/checkout', title: 'ชำระเงิน' },
        first_page: 'https://cleanfitdelivery.com/order',
        referrer: 'https://www.google.com/search',
        utm: null,
        last_utm: null,
        device: 'desktop',
        identified: false,
        first_seen_at: iso(2 * 24 * 60 * MIN),
        last_seen_at: iso(2 * 24 * 60 * MIN - 20 * MIN),
        context: null,
      },
    ],
  ])
}

/** Same defaults as api/app/webchat/questions.py DEFAULT_QUESTIONS. */
export const ASK_AREA_TEXT =
  'ค่าส่งและวันที่ได้รับอาหารขึ้นอยู่กับพื้นที่จัดส่งค่ะ รบกวนคุณลูกค้าแจ้งเขตหรืออำเภอ พร้อมรหัสไปรษณีย์ของที่อยู่จัดส่งให้แอดมินได้ไหมคะ แอดมินจะเช็กให้เลยค่ะ'
export const HOW_TO_ORDER_TEXT =
  'สั่งซื้อผ่านหน้าเว็บได้ใน 3 ขั้นตอนค่ะ\n1. เลือกแพ็กเกจ\n2. เลือกเมนู\n3. กรอกที่อยู่และชำระเงิน\nหรือจะพิมพ์บอกแอดมินในแชทนี้ก็ได้เลยนะคะ'
export const DEFAULT_QUESTIONS: WebchatQuestion[] = [
  { id: 'payment_failed', text: 'ชำระเงินไม่ผ่าน', on: true, answer: 'staff', reply: '', tag: 'ชำระเงินไม่ผ่าน',
    pages: ['/order*/checkout', '/order*/payment', '/order*/promptpay', '/order*/order'] },
  { id: 'help_pick', text: 'ช่วยเลือกเมนูให้หน่อย', on: true, answer: 'staff', reply: '', tag: null, pages: ['/order*/menu'] },
  { id: 'menu', text: 'ขอดูเมนูรอบนี้', on: true, answer: 'menu', reply: '', tag: null, pages: [] },
  { id: 'packages', text: 'แพ็กเกจราคาเท่าไร', on: true, answer: 'packages', reply: '', tag: null, pages: [] },
  { id: 'how_to_order', text: 'สั่งซื้อยังไง', on: true, answer: 'text', reply: HOW_TO_ORDER_TEXT, tag: null, pages: [] },
  { id: 'shipping_fee', text: 'ค่าส่งไปบ้านเท่าไร', on: true, answer: 'ask_area', reply: ASK_AREA_TEXT, tag: null, pages: [] },
  { id: 'delivery_day', text: 'ได้ของวันไหน', on: true, answer: 'ask_area', reply: ASK_AREA_TEXT, tag: null, pages: [] },
]

export const MOCK_WEBCHAT_SETTINGS: WebchatSettings = {
  enabled: true,
  site_name: 'Cleanfit',
  welcome_text: 'สวัสดีค่ะ สอบถามเรื่องเมนู คำสั่งซื้อ หรือการจัดส่ง พิมพ์ข้อความถึงแอดมินได้เลยนะคะ',
  offline_text: 'ฝากข้อความไว้ได้เลยนะคะ แอดมินจะรีบตอบกลับโดยเร็วค่ะ',
  ask_contact: true,
  hours_mode: 'always',
  open_days: [0, 1, 2, 3, 4, 5, 6],
  open_time: '08:00',
  close_time: '22:00',
  questions: DEFAULT_QUESTIONS.map((q) => ({ ...q, pages: [...q.pages] })),
  email_notify: false,
  ask_email: true,
}

const DAY_ABBR = ['จ.', 'อ.', 'พ.', 'พฤ.', 'ศ.', 'ส.', 'อา.']

/** Same wording as api/app/webchat/store.py hours_text. */
export function hoursText(s: Pick<WebchatSettings, 'hours_mode' | 'open_days' | 'open_time' | 'close_time'>): string | null {
  if (s.hours_mode !== 'set') return null
  const text = `เวลาทำการ ${s.open_time}–${s.close_time} น.`
  const d = [...s.open_days].sort((a, b) => a - b)
  if (d.length === 7) return `${text} ทุกวัน`
  const first = d[0]
  const last = d[d.length - 1]
  if (first !== undefined && last !== undefined && d.length > 2 && last - first + 1 === d.length) {
    return `${text} (${DAY_ABBR[first]}–${DAY_ABBR[last]})`
  }
  return `${text} (${d.map((x) => DAY_ABBR[x]).join(' ')})`
}

export function mockSettingsResponse(s: WebchatSettings): WebchatSettingsResponse {
  const base = typeof location !== 'undefined' ? location.origin : 'https://cleanfit-chat.example'
  return {
    settings: { ...s, open_days: [...s.open_days], questions: s.questions.map((q) => ({ ...q, pages: [...q.pages] })) },
    editable: Object.keys(s) as (keyof WebchatSettings)[],
    status: {
      configured: true,
      online_now: true,
      hours_text: hoursText(s),
      origins: ['https://cleanfit.cloud', 'https://cleanfitdelivery.com', 'https://www.cleanfit.cloud', 'https://www.cleanfitdelivery.com'],
      identity: false,
      email: { mode: 'allowlist', allowlist: 1, configured: true, sender: 'Cleanfit Delivery <noreply@cleanfitdelivery.com>',
        asking: s.email_notify && s.ask_email },
    },
    embed: {
      script_url: `${base}/webchat.js`,
      snippet: `<script src="${base}/webchat.js" async></script>`,
      demo_url: `${base}/webchat-demo.html`,
    },
    questions_meta: { menu: 'poster', tags: ['แพ้อาหาร', 'รอชำระ', 'VIP', 'สนใจแพ็กใหญ่', 'ลูกค้าใหม่', 'ชำระเงินไม่ผ่าน'] },
  }
}

/** The server's checks (api/app/webchat/questions.py clean_questions) for the mock API. Thai error or null. */
export function questionsProblem(qs: WebchatQuestion[]): string | null {
  if (qs.length > 10) return 'ปุ่มคำถามได้ไม่เกิน 10 ปุ่ม'
  for (const q of qs) {
    const text = q.text.trim()
    if (!text) return 'ใส่ข้อความบนปุ่มคำถามทุกปุ่ม'
    if (q.pages.some((p) => !/^\/[A-Za-z0-9/_.~%*-]*$/.test(p.trim()))) {
      return `ปุ่ม “${text}”: หน้าเว็บต้องขึ้นต้นด้วย / เช่น /order*/menu (ใช้ * แทนส่วนใดก็ได้)`
    }
    if (q.answer === 'text' && q.on && !q.reply.trim()) return `ปุ่ม “${text}”: ใส่ข้อความที่จะตอบ หรือเลือกให้ทีมงานตอบ`
  }
  return null
}
